import { useResults } from "../data/ResultsContext";
import { Calendar, ChevronDown } from "lucide-react";
import { useState } from "react";

/**
 * Subtle run-date selector shown above the Findings section.
 * Only renders when there are >1 runs in the archive.
 * When there's just one run (the latest), it shows a quiet indicator.
 */
export default function RunSelector() {
  const { runs, activeRunId, setActiveRunId, isArchive } = useResults();
  const [open, setOpen] = useState(false);

  // If only 1 run exists, show a minimal status indicator
  if (runs.length <= 1) {
    return (
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] text-neutral-500 uppercase">
        <Calendar className="w-3 h-3" />
        <span>{runs[0]?.date || "Latest"}</span>
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D00] animate-pulse" />
        <span className="text-[#FF4D00]">Live</span>
      </div>
    );
  }

  const activeLabel = runs.find((r) => r.id === activeRunId)?.label || "Latest";

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase border border-white/10 hover:border-[#FF4D00]/40 px-3 py-2 transition-colors group"
      >
        <Calendar className="w-3 h-3 text-neutral-500 group-hover:text-[#FF4D00] transition-colors" />
        <span className={isArchive ? "text-amber-400" : "text-neutral-300"}>
          {activeLabel}
        </span>
        {isArchive && (
          <span className="text-[8px] bg-amber-400/15 text-amber-400 border border-amber-400/30 px-1.5 py-0.5 tracking-[0.15em]">
            ARCHIVE
          </span>
        )}
        {!isArchive && (
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF4D00] animate-pulse" />
        )}
        <ChevronDown className={`w-3 h-3 text-neutral-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          {/* Dropdown */}
          <div className="absolute top-full mt-1 left-0 z-50 min-w-[280px] border border-white/10 bg-[#0a0a0a] shadow-2xl shadow-black/50">
            <div className="px-3 py-2 border-b border-white/5 font-mono text-[9px] tracking-[0.3em] text-neutral-600 uppercase">
              Benchmark Runs
            </div>
            {runs.map((run) => (
              <button
                key={run.id}
                onClick={() => { setActiveRunId(run.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 font-mono text-[10px] tracking-[0.18em] transition-colors flex items-center gap-2 ${
                  run.id === activeRunId
                    ? "bg-[#FF4D00]/10 text-[#FF4D00] border-l-2 border-[#FF4D00]"
                    : "text-neutral-400 hover:bg-white/5 hover:text-neutral-200 border-l-2 border-transparent"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${
                  run.id === "latest" ? "bg-[#FF4D00]" : "bg-neutral-600"
                }`} />
                <span>{run.label}</span>
                {run.id === "latest" && (
                  <span className="ml-auto text-[8px] text-[#FF4D00]/60 tracking-[0.2em]">LIVE</span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
